import type { CSSProperties, ReactNode } from 'react'

import { Pill, Tier } from '../ui'
import type { Condition } from '../../lib/api'
import { deviceLabel } from '../../lib/devices'
import { formatScore } from '../../lib/format'
import { CONDITION_WORDS } from '../../lib/recordings'
import { labelTone } from '../../lib/labelTone'
import { WARNING_COPY } from '../../lib/warnings'
import type { ScoreCard as ScoreCardData, SlotReason } from '../../lib/dailyStatus'
import { References } from './References'
import { ASSISTANT_ENABLED } from '../../lib/features'
import { AskAboutThis } from '../assistant/AskAboutThis'

/**
 * One score, as the payload gives it, in one of four shapes.
 *
 * ── THREE STATES, AND THE MIDDLE ONE IS THE POINT ───────────────────────────
 *
 * 1. **A value.** The number, the payload's label under it, and the payload's
 *    title and description around it.
 * 2. **No value, no reason.** Six of the eight slots are in this state whenever
 *    they are suppressed: the package gates only two families, so for the rest
 *    there *is* no reason in the payload and inventing one would be the client
 *    making a scientific claim. The card still renders, because §1.4 asks for
 *    "not available for this session" as a state rather than a missing row.
 * 3. **No value, with the package's own reason.** Only `activation_arousal` and
 *    `coupling_change`, and only when both the validity flag and its paired
 *    warning token agree. See `SLOT_VALIDITY` in `lib/dailyStatus.ts`.
 * 4. **Not measured by this headband** (Sprint 1.9.2 S4). `validity.unavailable`
 *    names the slot: the montage lacks the electrodes the measure needs, so no
 *    recording from this device will ever carry it. The card is then the
 *    payload's SENTENCE for that reason (`validity.reason_text`) and nothing
 *    else in the value's place — no number, no ring, no track, no neutral
 *    anything, and not the session sentence of state 2, which would say the
 *    wrong thing about why. A token the payload sent without words is shown
 *    raw, as a `.tag`, never as a blank.
 * 5. **Withheld for low signal quality** (FIRST-DAY-01). `validity.withheld`
 *    names the slot: this headband DOES produce the measure, but this
 *    recording was too noisy to compare it with the baseline. The payload's
 *    sentence replaces the session sentence as in state 4, with the class
 *    `--withheld` and never `--device`, and the ring KEEPS its dashed track:
 *    the dial rule is keyed on the device reason alone, because a withheld
 *    measure is a scale this recording could not be placed on, not one that
 *    does not exist. One slot, one reason: a device reason wins, and the read
 *    model already drops a withheld reason beside a gated token (state 3).
 *
 * A number the payload ships WITH a reservation (`validity.caveats`) keeps its
 * number and gets the payload's sentence beside it.
 *
 * **Nothing here is computed.** No threshold, no rounding that changes a value,
 * no colour chosen by magnitude. The label's tone comes from `labelTone`, which
 * maps the payload's own three-word vocabulary onto three neutral colours, and
 * the evidence tier is a string the backend sent.
 *
 * The title is `display.title` and never the slot key: `cognitive_engagement`
 * renders as "Anterior–posterior spectral balance", which is the name the Status
 * Guide gives it and the only one that is not an over-claim. Since eeg-tracking
 * 0.14.0 a reviewed measure also carries `display.user_title` ("Thinking vs.
 * resting balance"), which then leads, with `title` kept beside it in smaller,
 * muted text and `display.user_summary` beneath. Both are the package's words.
 *
 * ── FOUR VARIANTS, ONE COMPONENT ────────────────────────────────────────────
 *
 * `full` is the shape 1.9 shipped and is what `ResearchSection` still renders.
 * `ring`, `row` and `recording` are the Daily Status body: signal quality is
 * the recording card, the other six are a dial each in the hero and a detail
 * row each below it. R13 keeps `.score-card`, `.score-card__number`, the
 * `<h3>` title and `article[aria-labelledby]` on all four, and keeps this the
 * only component that renders a score.
 *
 * **A ring and a row are one slot drawn twice**, so their heading ids differ
 * (`score-ring-<slot>` and `score-<slot>`) — two elements with one id is a
 * broken `aria-labelledby` on whichever the browser finds second.
 *
 * ── NO JS ARITHMETIC ON A SCORE (R14) ───────────────────────────────────────
 *
 * The payload number goes onto the element as a custom property, raw:
 * `style={{ '--v': value }}`. Every piece of geometry — the arc length, the
 * marker's position along the track, the tick's angle — is a CSS `calc()` or
 * `clamp()` in the stylesheet. There is no rounding, no division and no clamp
 * in TypeScript, and `formatScore` remains the only thing that turns a score
 * into characters. CSP allows inline style (`style-src 'unsafe-inline'`).
 *
 * When the value is null there is NO `style` attribute at all — never
 * `--v: 0`, which would draw an arc the length of a real zero.
 */

export type ScoreCardVariant = 'full' | 'ring' | 'row' | 'recording'

/** What the recording card says about the session itself. */
export interface ScoreCardRecording {
  condition: Condition
  deviceId?: string | null
}


/**
 * The payload's label, in the payload's words, in a tone `labelTone` chose.
 *
 * The words are ALWAYS beside the mark, so the colour is never the only
 * channel (WCAG 1.4.1). `dot` is the mark.
 */
function Label({ label, labelCode }: { label: string | null; labelCode: string | null }) {
  if (label === null) return null
  return (
    <Pill tone={labelTone(labelCode)} dot>
      {label}
    </Pill>
  )
}

/**
 * `evidence_tier`, as text, keyed on the payload's enum.
 *
 * `moderate` and anything the backend has not shipped yet render plain — never
 * dropped, and never thrown on. The word "evidence" is the client's, and it
 * names the vocabulary rather than making a claim inside it.
 */
function TierMark({ tier }: { tier: string }) {
  return <Tier level={tier}>{tier} evidence</Tier>
}

/**
 * R18: "What this does not mean:" is VISIBLE text now.
 *
 * It was `visually-hidden`, which meant the one sentence that stops a number
 * being read as a verdict arrived unlabelled for everybody reading with their
 * eyes. The payload sentence itself is rendered verbatim; rewording it for a
 * reader is backend ask B4.
 */
function Precaution({ precaution }: { precaution: string }) {
  return (
    <p className="score-card__precaution">
      <b>What this does not mean:</b> {precaution}
    </p>
  )
}

/**
 * The longer disclosure, closed by default.
 *
 * `refs` is a list of citation IDs. They are resolved to the works they name by
 * `References` (V1-INTEGRATION step 5.5), and the item spans the whole grid
 * row: a citation is a line of prose, not a figure that fits a 200px cell.
 */
function Evidence({ evidence }: { evidence: NonNullable<ScoreCardData['evidence']> }) {
  return (
    <details className="score-card__evidence">
      <summary>What this measures</summary>
      <dl className="detail-grid">
        <div className="detail-grid__item">
          <dt className="record__label">A change means</dt>
          <dd className="record__value">{evidence.state_change}</dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">Measured from</dt>
          <dd className="record__value">{evidence.meaning}</dd>
        </div>
        <div className="detail-grid__item">
          <dt className="record__label">What the research says</dt>
          <dd className="record__value">{evidence.literature}</dd>
        </div>
        {evidence.refs.length > 0 && (
          <div className="detail-grid__item score-card__refs-item">
            <dt className="record__label">References</dt>
            <dd className="record__value">
              <References refs={evidence.refs} />
            </dd>
          </div>
        )}
      </dl>
    </details>
  )
}

/**
 * The package's own attribution for a missing value.
 *
 * The emptiness guard stays at every CALL SITE, written as
 * `card.unavailableReasons.length > 0`, because that exact expression is the
 * row `clientScience.test.ts` carries a written reason for — an allowlist keyed
 * on the expression's own text is only as good as the text staying put.
 */
function Reasons({ reasons }: { reasons: readonly string[] }) {
  return (
    <ul className="score-card__reasons">
      {reasons.map((code) => (
        <li key={code}>{WARNING_COPY[code] ?? <code className="mono-cell">{code}</code>}</li>
      ))}
    </ul>
  )
}

/**
 * A reason token in the payload's words, or the token itself when the payload
 * sent it without any (a pre-0.10.0 result). Never blank, and never a sentence
 * of the client's: a guessed explanation of why a headband cannot measure
 * something is a scientific claim.
 */
function ReasonWords({ reason }: { reason: SlotReason }) {
  return <>{reason.sentence ?? <code className="tag">{reason.token}</code>}</>
}

/**
 * §1.4's state, in the words the spec names. Never a dash, never a zero.
 *
 * With a `reason` the payload's sentence REPLACES the session sentence rather
 * than joining it. `kind` says which map named the slot, and the class says it
 * to the stylesheet and the specs:
 *
 * - `device` — `validity.unavailable`: no session on this headband will ever
 *   carry the number, so "not available for this session" would say the wrong
 *   thing about why.
 * - `withheld` — `validity.withheld`: this recording was too noisy to compare
 *   the measure with the baseline. Never `--device`, which reads as "this
 *   headband cannot measure it".
 */
function Unavailable({ reason, kind }: { reason: SlotReason | null; kind: 'device' | 'withheld' }) {
  if (reason === null) {
    return <p className="score-card__unavailable">Not available for this session</p>
  }
  return (
    <p className={`score-card__unavailable score-card__unavailable--${kind}`} data-reason={reason.token}>
      <ReasonWords reason={reason} />
    </p>
  )
}

/** `validity.caveats[slot]`: beside the number it qualifies, never instead of it. */
function Caveat({ caveat }: { caveat: SlotReason }) {
  return (
    <p className="score-card__caveat" data-reason={caveat.token}>
      <ReasonWords reason={caveat} />
    </p>
  )
}

/**
 * The payload's own mark on this slot's scale — O2, and nothing else.
 *
 * `scale_baseline` does not exist on the wire yet (B1), so this renders on no
 * payload the deployed backend produces. It carries NO text, no caption and no
 * gutter label: any word naming it would be a client-authored claim about what
 * the number means, which is why B1 also asks for `scale_baseline_label`.
 *
 * The geometry is CSS. `--b` is the payload number, raw.
 */
/**
 * The words that name the mark — BOTH of them the payload's.
 *
 * The number is `display[slot].scale_baseline` and the words are
 * `display[slot].scale_baseline_label`, rendered with nothing between them but a
 * separator. O2 forbids the CLIENT saying what the position means, and until
 * schema 1.4 there was no way to say it at all without doing exactly that, so
 * the mark shipped silent and unexplained.
 *
 * DEVIATION FROM THE CANVAS, recorded rather than silent: `Today · desktop`
 * draws `tick at 50 · your baseline`. The leading "tick at" is the client
 * narrating its own drawing, and this is the one place in the app where that
 * distinction is the whole point, so only the payload's two values render. The
 * separator is presentation, like the roll-up line's (R15).
 *
 * Renders nothing without the words. A bare number under a ring would be a
 * second value competing with the score.
 */
function BaselineNote({ at, label }: { at: number; label: string | null }) {
  if (label === null) return null
  return (
    <p className="score-card__note">
      {formatScore(at)} · {label}
    </p>
  )
}

function BaselineMark({ at, className }: { at: number; className: string }) {
  return <i className={className} aria-hidden="true" style={{ '--b': at } as CSSProperties} />
}

interface ScoreCardProps {
  card: ScoreCardData
  variant?: ScoreCardVariant
  /**
   * `display[slot].scale_baseline`, passed straight through from the read
   * model. Absent or null → no mark. NEVER a literal (O2).
   */
  baselineAt?: number | null
  /** The recording card's visual: the Pro montage (`BrainSlot`) on a BrainBit Pro day, none otherwise. */
  media?: ReactNode
  /** Recording-card facts that come from the ROW, not from the payload. */
  recording?: ScoreCardRecording
}

export function ScoreCard({
  card,
  variant = 'full',
  baselineAt,
  media,
  recording,
}: ScoreCardProps) {
  // The client may not invent a title, so a slot with no display block is not a
  // card. This does not happen on any payload the deployed backend produces.
  if (card.display === null) return null

  // Read into a local so the narrowing survives into the JSX below. `card.value`
  // is a property access, and TypeScript will not carry `card.value === null`
  // through a separate boolean into the branch that needs a `number`.
  const value = card.value
  const unavailable = value === null
  // `validity.unavailable[slot]`: this HEADBAND cannot produce the measure.
  // `readDailyStatus` (for a measure) and `uiHeroCards` (for an interface card)
  // have already nulled `value` when this is set.
  const deviceReason = card.unavailable
  // The one reason the card's sentence slot carries: the device's, or else the
  // recording's (`validity.withheld`). One slot, one reason.
  const reason = deviceReason ?? card.withheld
  const reasonKind = deviceReason !== null ? 'device' : 'withheld'
  const headingId = variant === 'ring' ? `score-ring-${card.slot}` : `score-${card.slot}`
  const className = [
    'score-card',
    `score-card--${variant}`,
    unavailable && 'score-card--unavailable',
  ].filter(Boolean).join(' ')

  // eeg-tracking 0.14.0: a reviewed measure carries a plain name (`user_title`)
  // and its one-line meaning (`user_summary`). The plain name leads; the
  // technical `title` stays inside the heading, smaller and muted, so the
  // accessible name still carries both. Without `user_title` the heading is
  // `title` alone, exactly as before. A dial has a third of a row, so its
  // technical label is the payload's `short_title` where there is one; the
  // detail row beneath it carries the full `title`.
  const userTitle = card.display.userTitle
  const technical = variant === 'ring'
    ? (card.display.shortTitle ?? card.display.title)
    : card.display.title
  const title = (
    <h3 className="score-card__title" id={headingId}>
      {userTitle ?? card.display.title}
      {userTitle !== undefined && (
        <>
          {' '}
          <span className="score-card__technical">{technical}</span>
        </>
      )}
    </h3>
  )
  const summary = card.display.userSummary !== undefined
    ? <p className="score-card__summary">{card.display.userSummary}</p>
    : null
  const tier = card.display.evidence_tier ? <TierMark tier={card.display.evidence_tier} /> : null
  // agent-1-web W4: the measure, as this card names it, for the assistant's
  // "Ask about this". Behind the module-scope flag, so a build without the
  // assistant has none of it; the card's own markup is otherwise unchanged.
  const ask = ASSISTANT_ENABLED ? (
    <AskAboutThis measure={{ ref: card.slot, title: userTitle ?? card.display.title }} />
  ) : null
  const mark = baselineAt == null || value === null ? null : baselineAt

  if (variant === 'ring') {
    return (
      <article className={className} aria-labelledby={headingId}>
        {/* NO DIAL AT ALL for a measure this headband cannot produce. An empty
            track is still a dial: it reads as a scale this recording sat at
            the bottom of, and there is no scale here — only the payload's
            sentence for why (Sprint 1.9.2 S4). The session-null state keeps
            its dashed track, as it always has. */}
        {deviceReason === null && (
          <div
            className={`ring ring--${labelTone(card.labelCode)}${unavailable ? ' ring--na' : ''}`}
            style={value !== null ? ({ '--v': value } as CSSProperties) : undefined}
          >
            <svg className="ring__svg" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
              <circle className="ring__track" cx="50" cy="50" r="44" />
              {value !== null && <circle className="ring__arc" cx="50" cy="50" r="44" />}
            </svg>
            {mark !== null && <BaselineMark at={mark} className="ring__tick" />}
            {/* NO number element at all when there is no value — not an empty
                one, and never an em dash. `DailyStatusView.test.tsx:18-19` names
                "a dash that looks like a value" as the failure this state
                exists to avoid. */}
            {value !== null && (
              <span className="ring__value score-card__number">{formatScore(value)}</span>
            )}
          </div>
        )}
        {title}
        {/* The sentence ALONE. The row beneath carries the attributable reason
            (RULINGS Corrections R14) — one slot, one reason, not two. */}
        {value === null && <Unavailable reason={reason} kind={reasonKind} />}
        <div className="score-card__foot">
          {value !== null && <Label label={card.label} labelCode={card.labelCode} />}
          {tier}
        </div>
        {mark !== null && <BaselineNote at={mark} label={card.display.scaleBaselineLabel ?? null} />}
        {ask}
      </article>
    )
  }

  if (variant === 'row') {
    return (
      <article className={className} aria-labelledby={headingId}>
        <div className="score-card__about">
          {title}
          {summary}
          <p className="score-card__description">{card.display.description}</p>
          {card.evidence !== null && <Evidence evidence={card.evidence} />}
          {ask}
        </div>
        <div className="score-card__read">
          <div className="score-card__top">
            {value === null ? (
              <Unavailable reason={reason} kind={reasonKind} />
            ) : (
              <span className="score-card__number">{formatScore(value)}</span>
            )}
            <div className="score-card__foot">
              <Label label={card.label} labelCode={card.labelCode} />
              {tier}
            </div>
          </div>
          {value !== null && (
            <>
              <div className="score-card__track" style={{ '--v': value } as CSSProperties}>
                <i className="score-card__marker" aria-hidden="true" />
                {mark !== null && <BaselineMark at={mark} className="score-card__baseline" />}
              </div>
              {/* Two labels, like the trend's gutter. The ends are the DOMAIN
                  and are the client's to draw; the midpoint is a measurement
                  and is not, so it is named by the payload beneath the track
                  or not at all (O2). */}
              <div className="score-card__scale" aria-hidden="true">
                <span>0</span>
                <span>100</span>
              </div>
              {mark !== null && (
                <BaselineNote at={mark} label={card.display.scaleBaselineLabel ?? null} />
              )}
            </>
          )}
          {card.caveat !== null && <Caveat caveat={card.caveat} />}
          {card.unavailableReasons.length > 0 && <Reasons reasons={card.unavailableReasons} />}
          {card.evidence !== null && card.evidence.precaution && (
            <Precaution precaution={card.evidence.precaution} />
          )}
        </div>
      </article>
    )
  }

  if (variant === 'recording') {
    // The ROW's resolved device, named with its contact count from the one
    // device mapping (`lib/devices.ts`). An id this build does not know renders
    // NO meta line: a channel count guessed from an unknown device is a fact
    // about the recording the client does not have.
    const deviceWords = deviceLabel(recording?.deviceId)
    return (
      <article className={className} aria-labelledby={headingId}>
        <div className="card__head">
          <div className="card__eyebrow">This recording</div>
          {deviceWords !== null && <span className="card__meta">{deviceWords}</span>}
        </div>
        {media}
        {/* One wrapper so the text can sit beside the brain on the page grid (UI-LAYOUT-01);
            it inherits the card's gap, so below 1024px nothing moves. */}
        <div className="score-card__text">
        {title}
        {summary}
        <div className="score-card__caption">
          {recording !== undefined && (
            <Pill>
              <span className="score-card__condition-words">{CONDITION_WORDS[recording.condition]} · </span>
              {recording.condition}
            </Pill>
          )}
          {value === null ? (
            <Unavailable reason={reason} kind={reasonKind} />
          ) : (
            <Pill tone={labelTone(card.labelCode)} dot>
              <span className="score-card__number">{formatScore(value)}</span>
              {card.label !== null && <> · {card.label}</>}
            </Pill>
          )}
          {tier}
        </div>
        {card.caveat !== null && <Caveat caveat={card.caveat} />}
        {card.unavailableReasons.length > 0 && <Reasons reasons={card.unavailableReasons} />}
        <p className="score-card__description">{card.display.description}</p>
        {card.evidence !== null && card.evidence.precaution && (
          <Precaution precaution={card.evidence.precaution} />
        )}
        {card.evidence !== null && <Evidence evidence={card.evidence} />}
        </div>
      </article>
    )
  }

  return (
    <article className={className} aria-labelledby={headingId}>
      <header className="score-card__head">
        {title}
        {tier}
      </header>
      {summary}

      {value === null ? (
        <Unavailable reason={reason} kind={reasonKind} />
      ) : (
        <p className="score-card__value">
          <span className="score-card__number">{formatScore(value)}</span>
          <Label label={card.label} labelCode={card.labelCode} />
        </p>
      )}

      {card.caveat !== null && <Caveat caveat={card.caveat} />}
      {card.unavailableReasons.length > 0 && <Reasons reasons={card.unavailableReasons} />}

      <p className="score-card__description">{card.display.description}</p>

      {/* Beside the number, not in a footer. §Notes asks for it there, and the
          Status Guide gives every status a "precautions" line for the same
          reason: the caveat is what stops the number being read as a verdict. */}
      {card.evidence !== null && card.evidence.precaution && (
        <Precaution precaution={card.evidence.precaution} />
      )}

      {card.evidence !== null && <Evidence evidence={card.evidence} />}
      {ask}
    </article>
  )
}
